<template>
	<Teleport v-if="mode" to="body" :disabled="mode === 'form'">
		<wrap-if :test="mode === 'modal'" tag="div" cls="backdrop">
		<dialog class="dialog closeable" ref="dialog" :style="dialogStyle" :class="[animationClass, modeClass]" @close="dialogClosed" v-bind="$attrs">
			<close-cross v-if="mv" class="no-red close-top-right-corner" @click.prevent.stop="close"></close-cross>
			<div v-if="showDialogHeader" class="dialog-head" @mousedown="dialogMoveStart">
				<div class="dialog-head-left">
					<slot v-if="$slots.header" name="header"></slot>
					<template v-else>
						<sym-icon v-if="icon" class="dialog-head-icon" :name="icon"></sym-icon>
						<span v-if="title" class="dialog-head-title">{{ title }}</span>
					</template>
				</div>
				<div class="dialog-head-right"><slot name="head_controls"></slot></div>
			</div>
			<div v-if="globalErrors" class="dialog-error-body e2e is-container"><slot name="errors"></slot></div>
			<div class="dialog-body" :class="{ 'e2e' : e2e, 'top-content':hasTopContent, 'bottom-content':hasBottomContent }" ref="dialogBody" @scroll="updateShadows"><slot></slot></div>
			<div class="dialog-foot" v-if="$slots.status || $slots.secondary_controls || $slots.controls">
				<div class="dialog-foot-left"><slot name="status"></slot></div>
				<div class="dialog-foot-center"><slot name="secondary_controls"></slot></div>
				<div class="dialog-foot-right"><slot name="controls"></slot></div>
			</div>
			<resize-corner v-if="rs" @mousedown="dialogResizeStart"></resize-corner>
		</dialog>
		</wrap-if>
	</Teleport>
</template>

<script setup lang="ts">
import {ref, computed, onMounted, onUnmounted, type CSSProperties, watch, nextTick, useSlots} from "vue";
import SymIcon from "@fd/helpers/SymIcon.vue";
import CloseCross from "@fd/editors/file/CloseCross.vue";
import type {DialogMode} from "@t/ui/Dialog.ts";
import {getElementMeasure} from "@fd/helpers/DomUtils.ts";
import ResizeCorner from "@/dialog/ResizeCorner.vue";
import WrapIf from "@/ui/WrapIf.vue";

const props = withDefaults(defineProps<{
	icon?: string,
	title?: string,
	moveable?: boolean,
	resizable?: boolean,
	/** Edge to edge body content (left and right padding isn't set for the body). Use var(--form-h-start-padding) */
	e2e?: boolean,
	/** Atomatically show the window without the need to call show() func */
	show?: DialogMode,
	/** Initial dialog width. If it is bigger than screen it will be reduced */
	width?: number,
	globalErrors?: boolean
}>(), {
	moveable: true,
	resizable: true,
	e2e: false,
	width: 0,
	globalErrors: false
});
const emit = defineEmits(["close"]);

const dialog = ref<HTMLDialogElement|null>(null);
const dialogBody = ref<HTMLDivElement|null>(null);
const hasTopContent = ref<boolean>(true);
const hasBottomContent = ref<boolean>(false);
const mode = ref<DialogMode|null>(null);
const mv = computed<boolean>(() => mode.value != null && mode.value !== "form" && !!props.moveable);
const rs = computed<boolean>(() => mode.value != null && mode.value !== "form" && !!props.resizable);
const animationClass = ref<string|null>(null);
const modeClass = computed<string>(() => mode.value == null ? "mode-form" : ("mode-" + mode.value));

const MAX_DIALOG_WIDTH = document.documentElement.clientWidth - 16;
const top = ref<number>(0);
const left = ref<number>(0);
const width = ref<number>(props.width > MAX_DIALOG_WIDTH ? MAX_DIALOG_WIDTH : props.width);
const height = ref<number>(0);
const startTop = ref<number>(0);
const startLeft = ref<number>(0);
const startWidth = ref<number>(0);
const startHeight = ref<number>(0);
const startMouseX = ref<number>(0);
const startMouseY = ref<number>(0);

const slots = useSlots();
const showDialogHeader = computed<boolean>(() => !!slots.header || !!props.icon || !!props.title);
const showDialogGlobalErrors = computed<boolean>(() => !!slots.errors);

function dialogMoveStart(e:MouseEvent) {
	e.stopPropagation();
	e.preventDefault();
	if (!mv.value) return;
	startMouseX.value = e.clientX;
	startMouseY.value = e.clientY;
	startLeft.value = left.value;
	startTop.value = top.value;
	document.addEventListener("mousemove", drag);
	document.addEventListener("mouseup", dragEnd);
}

function dialogResizeStart(e:MouseEvent) {
	e.stopPropagation();
	e.preventDefault();
	if (!rs.value) return;
	const m = getElementMeasure(dialog.value);
	startMouseX.value = e.clientX;
	startMouseY.value = e.clientY;
	startWidth.value = m.width;
	startHeight.value = m.height;
	document.addEventListener("mousemove", resize);
	document.addEventListener("mouseup", resizeEnd);
}

function drag(e: MouseEvent) {
	let diffX = e.clientX - startMouseX.value;
	let diffY = e.clientY - startMouseY.value;
	left.value = startLeft.value + diffX;
	top.value = startTop.value + diffY;

}

function resize(e: MouseEvent) {
	let diffX = e.clientX - startMouseX.value;
	let diffY = e.clientY - startMouseY.value;
	width.value = startWidth.value + diffX;
	height.value = startHeight.value + diffY;

}

function dragEnd(e: MouseEvent) {
	document.removeEventListener("mousemove", drag);
	document.removeEventListener("mouseup", dragEnd);
}

function resizeEnd(e: MouseEvent) {
	document.removeEventListener("mousemove", resize);
	document.removeEventListener("mouseup", resizeEnd);
}

const dialogStyle = computed<CSSProperties>(() => {
	const s:CSSProperties = {};
	if (mode.value == null) return s;
	if (mode.value === "form") {
		s.position = "relative";
	} else {
		s.top = Math.round(top.value) + "px";
		s.left = Math.round(left.value) + "px";
		if (width.value !== 0) {
			s.width = Math.max(Math.round(width.value), 200) + "px";
		}
		if (height.value !== 0) {
			s.height = Math.max(Math.round(height.value), 200) + "px";
		}
	}

	return s;
});

function fastClose() {
	if (dialog.value) dialog.value.close();
	emit("close");
}

function close(e?:MouseEvent) {
	if (dialog.value) {
		if (mode.value === "form") {
			animationClass.value = null;	// no animation
			fastClose();
		} else {
			animationClass.value = "dialog-animation dialog-close-start";
			nextTick(() => {
				animationClass.value = "dialog-animation dialog-close-end";
				setTimeout(() => {
					fastClose();
				}, 301);
			});
		}
	}
}

function dialogClosed() {
	if (mode.value) {
		mode.value = null;
		emit("close");
	}
}

function show(withMode:DialogMode) {
	mode.value = withMode;	// enable and insert to dom
	if (mode.value === "form") animationClass.value = null;	// no animation
	else {
		animationClass.value = "dialog-animation dialog-open-start";
	}
	nextTick(() => {
		if (dialog.value) {
			if (dialog.value.open) dialog.value.close();
			dialog.value.show();
			setTimeout(() => {
				if (mode.value !== "form") {
					const dialogMeasure = getElementMeasure(dialog.value);
					//console.dir(dialogMeasure);
					left.value = (dialogMeasure.viewportWidth - (dialogMeasure.width / 0.8)) / 2;	// dialog is now 80% of its dimensions
					top.value = (dialogMeasure.viewportHeight - (dialogMeasure.height / 0.8)) / 3;
					animationClass.value = "dialog-animation dialog-open-end";
				}
				if (dialogBody.value) {
					const af = dialogBody.value.querySelector(".autofocus") as HTMLInputElement;
					af?.focus();
				}
			}, 0);
		}
	});
}

const resizeObserver = new ResizeObserver(updateShadows);

watch(dialogBody, (newBody, oldBody) => {
	if (newBody) resizeObserver.observe(newBody);
	else if (oldBody) resizeObserver.unobserve(oldBody);
});

function updateShadows() {
	if (dialogBody.value) {
		const atTop = dialogBody.value.scrollTop === 0;
		const atBottom = (dialogBody.value.scrollHeight - dialogBody.value.scrollTop - 2) <= dialogBody.value.clientHeight;

		hasTopContent.value = !atTop;
		hasBottomContent.value = !atBottom;
	}
}

function visible():boolean {
	if (dialog.value) return dialog.value.open;
	return false;
}

/**
 * Do short shake animation.
 * Call this method to provide a simple error-like feedback to the user.
 */
function shake() {
	animationClass.value = "shake-me";
	setTimeout(() => animationClass.value = null, 1200);
}

onMounted(() => {
	if (props.show) {
		show(props.show);
	}
});

defineExpose({
	show, close, visible, shake
});
</script>

<style>
.dialog-animation {
	transition: opacity 300ms ease-out, transform 300ms ease-out;
}
.dialog-open-start {
	opacity: 0;
	transform: translateY(32px) scale(0.8);
}
.dialog-open-end, .dialog-close-start {
	opacity: 1;
	transform: translateY(0px) scale(1);
}
.dialog-close-end {
	opacity: 0;
	transform: translateY(-16px) scale(0.8);
}

</style>